// react
import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'router';
import { useSelector } from 'react-redux';

// components
import {
  EligibilityResults,
  type BenefitItem,
} from '@karehero/llama/pwa/pages/EligibilityResults/EligibilityResults';
import { CareProfileCategoryID } from '@karehero/models';

// config
import { benefitsMap } from 'config/benefitsData';

// hooks
import { useCareProfile } from 'hooks';
import { useGoBack } from 'hooks/useGoBack';

// store
import { getCurrentCareCircle } from 'store/slices/careCircleSlice';
import {
  useGetEligibilitiesQuery,
  Eligibility,
  EligibilityStatus,
} from 'store/api/assessment';

const toBenefitItem = (e: Eligibility): BenefitItem => {
  const data = benefitsMap[e.fundingType];
  if (data) {
    return data as BenefitItem;
  }
  return {
    benefitName: e.fundingType,
    benefitDescription: '',
    icon: 'circle-sterling' as const,
    savingsMessage: '',
    eligibilityInformation: [],
    additionalBenefits: [],
    applicationInstructions: '',
    sources: [],
  };
};

export const BenefitsEligibilityComplete = () => {
  const navigate = useNavigate();
  const goBack = useGoBack();
  const { toLastFormPage, careRecipientFirstName, handleSubmit } =
    useCareProfile({
      categoryID: CareProfileCategoryID.EligibilityCalculator,
    });
  const currentCareCircle = useSelector(getCurrentCareCircle);
  const { data: eligibilities } = useGetEligibilitiesQuery(
    currentCareCircle?.id || '',
    { skip: !currentCareCircle?.id },
  );

  const eligibleBenefitItems: BenefitItem[] = useMemo(
    () =>
      (eligibilities || [])
        .filter(
          (e: Eligibility) =>
            e.eligibilityStatus === EligibilityStatus.Eligible,
        )
        .map(toBenefitItem),
    [eligibilities],
  );

  const ineligibleBenefitItems: BenefitItem[] = useMemo(
    () =>
      (eligibilities || [])
        .filter(
          (e: Eligibility) =>
            e.eligibilityStatus === EligibilityStatus.Ineligible,
        )
        .map(toBenefitItem),
    [eligibilities],
  );

  // Treat viewing as a submission
  useEffect(() => handleSubmit?.(), [handleSubmit]);

  return (
    <EligibilityResults
      onBack={goBack}
      title={`${careRecipientFirstName || 'Your Loved One'}'s Benefits Eligibility Results`}
      eligibleBenefitItems={eligibleBenefitItems}
      ineligibleBenefitItems={ineligibleBenefitItems}
      onContinueAssessment={() => navigate(ROUTES.FINANCIAL_STATUS)}
      onPrevious={() =>
        navigate(toLastFormPage, { replace: true, state: { back: true } })
      }
    />
  );
};

export default BenefitsEligibilityComplete;
